import {
  DateAndTimePicker,
  Icon,
  InlineMessage,
  Typography,
} from "@saviynt/design-system";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useTheme } from "@repo/mfe-core";
import {
  type Frequency,
  DEFAULT_FREQUENCY,
  FREQUENCY_OPTIONS,
} from "../../../utils/schedule";
import { getDefaultTime } from "./utils";
import { FormSection } from "../../FormSection";

const CHEVRON_SVG =
  "url(\"data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%239ea1b1' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

interface AdditionalConfigurationSectionProps {
  onScheduleChange: (frequency: string, time: Date | null) => void;
  initialTime?: Date | null;
  initialFrequency?: Frequency;
  isRequired?: boolean;
  isCritical?: boolean;
}

export function AdditionalConfigurationSection({
  onScheduleChange,
  initialTime,
  initialFrequency,
  isRequired = false,
  isCritical = false,
}: AdditionalConfigurationSectionProps) {
  const { theme: currentTheme } = useTheme();
  const intl = useIntl();

  const [frequency, setFrequency] = useState<Frequency>(
    initialFrequency ?? DEFAULT_FREQUENCY,
  );
  const [importTime, setImportTime] = useState<Date | null>(() => {
    if (initialTime !== undefined) return initialTime;
    return (initialFrequency ?? DEFAULT_FREQUENCY) === "24hours"
      ? getDefaultTime()
      : null;
  });

  const hasInitializedRef = useRef(false);
  const lastInitialTimeRef = useRef<Date | null | undefined>(undefined);
  const lastInitialFrequencyRef = useRef<Frequency | undefined>(undefined);

  const isDaily = frequency === "24hours";

  const selectClasses = [
    "w-full py-2 pl-3 pr-8 rounded-lg text-sm leading-5",
    "cursor-pointer appearance-none bg-no-repeat bg-[right_12px_center]",
    "focus:outline-none focus:ring-1",
  ].join(" ");

  // Theme-aware tokens (provided by @saviynt/tokens) handle light/dark
  // atomically via `data-theme`; avoid JS-branched fallbacks that briefly
  // paint with the wrong color during a theme flip.
  const selectStyle: React.CSSProperties = {
    backgroundImage: CHEVRON_SVG,
    color: "var(--color-foreground-neutral-bold)",
    backgroundColor: "var(--color-background-neutral-subtlest)",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "var(--color-border-neutral-subtle)",
  };

  // Fix datepicker theme in dark mode
  useEffect(() => {
    if (currentTheme === "dark") {
      const styleId = "datepicker-dark-fix";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .dark-theme-datepicker .react-datepicker__close-icon::after {
            filter: brightness(0) invert(1) !important;
          }
          .react-datepicker__time-box {
            background: var(--color-background-neutral-subtlest, #fff) !important;
          }
          .react-datepicker__time-list-item {
            color: var(--color-foreground-neutral-default, #e8e9ed) !important;
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, [currentTheme]);

  const handleFrequencyChange = (newFrequency: Frequency) => {
    setFrequency(newFrequency);
    if (newFrequency === "24hours") {
      const time = importTime ?? getDefaultTime();
      setImportTime(time);
      onScheduleChange(newFrequency, time);
    } else {
      onScheduleChange(newFrequency, null);
    }
  };

  const handleTimeChange = (time: Date | null) => {
    setImportTime(time);
    if (hasInitializedRef.current) {
      onScheduleChange(frequency, time);
    }
  };

  // Sync with parent initial values (edit mode prefill) and notify parent on mount
  useEffect(() => {
    let freqUpdated = false;
    let timeUpdated = false;

    // Update frequency if initial value changed
    if (
      initialFrequency !== undefined &&
      initialFrequency !== lastInitialFrequencyRef.current
    ) {
      setFrequency(initialFrequency);
      lastInitialFrequencyRef.current = initialFrequency;
      freqUpdated = true;
    }

    // Update time if initial value changed
    if (initialTime !== undefined) {
      const timeChanged =
        lastInitialTimeRef.current === undefined ||
        (initialTime === null && lastInitialTimeRef.current !== null) ||
        (initialTime !== null && lastInitialTimeRef.current === null) ||
        (initialTime instanceof Date &&
          lastInitialTimeRef.current instanceof Date &&
          initialTime.getTime() !== lastInitialTimeRef.current.getTime());

      if (timeChanged) {
        setImportTime(initialTime);
        lastInitialTimeRef.current = initialTime;
        timeUpdated = true;
      }
    }

    // Notify parent on first initialization
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const freq = initialFrequency ?? frequency;
      if (freq === "24hours") {
        const time =
          initialTime !== undefined
            ? initialTime
            : (importTime ?? getDefaultTime());
        onScheduleChange(freq, time);
      } else {
        onScheduleChange(freq, null);
      }
    } else if (freqUpdated || timeUpdated) {
      // Re-notify parent when edit-mode data arrives late
      const currentFreq = initialFrequency ?? frequency;
      if (currentFreq === "24hours") {
        onScheduleChange(currentFreq, initialTime ?? importTime);
      } else {
        onScheduleChange(currentFreq, null);
      }
    }
  }, [initialFrequency, initialTime]);

  const frequencyLabel =
    FREQUENCY_OPTIONS.find((o) => o.value === frequency)?.label ?? "";

  return (
    <FormSection
      icon="Settings"
      title={intl.formatMessage({
        id: "CfgyTo",
        defaultMessage: "Do you need to configure anything else?",
        description: "Additional configuration section title",
      })}
      isLast={true}
      className="p-4"
    >
      <div className="flex flex-col">
        <Typography kind="label" className="mb-3">
          <FormattedMessage
            id="VLAhCQ"
            defaultMessage="Import Schedule"
            description="Label for import schedule field"
          />
        </Typography>

        <div className="flex items-center gap-3">
          <span className="min-w-[80px] shrink-0 text-sm leading-5 text-[var(--color-foreground-neutral-subtle,#9ea1b1)] whitespace-nowrap">
            <FormattedMessage
              id="mjhKNY"
              defaultMessage="Sync every"
              description="Sync frequency dropdown label"
            />
          </span>
          <div className="flex-1">
            <select
              value={frequency}
              onChange={(e) =>
                handleFrequencyChange(e.target.value as Frequency)
              }
              className={selectClasses}
              style={selectStyle}
              aria-label={intl.formatMessage({
                id: "9szTMw",
                defaultMessage: "Sync frequency",
                description: "Aria label for sync frequency dropdown",
              })}
            >
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isDaily && (
          <div className="flex items-center gap-3 mt-1">
            <span className="min-w-[80px] shrink-0 text-sm leading-5 text-[var(--color-foreground-neutral-subtle,#9ea1b1)] whitespace-nowrap">
              <FormattedMessage
                id="B/sOoI"
                defaultMessage="Daily at"
                description="Daily schedule frequency"
              />
            </span>
            <div className="flex-1">
              <div
                className={
                  currentTheme === "dark" ? "dark-theme-datepicker" : ""
                }
              >
                <DateAndTimePicker
                  kind="timeStart"
                  startDate={importTime}
                  setStartDate={handleTimeChange}
                  minDate={new Date()}
                  maxDate={new Date()}
                  filterTime={() => true}
                  dateFormat="h:mm aa"
                  dataTheme={currentTheme === "dark" ? "dark" : "light"}
                  isRequired={isRequired}
                  isCritical={isCritical}
                  CriticalHelperText={
                    isCritical ? (
                      <InlineMessage
                        text={intl.formatMessage({
                          id: "xkUCy3",
                          defaultMessage: "Import time is required",
                          description: "Error message for required import time",
                        })}
                        colorTheme="critical"
                        size="small"
                        leftIcon={<Icon kind="AlertCriticalFilled" />}
                      />
                    ) : null
                  }
                />
              </div>
            </div>
            <span className="text-[var(--color-foreground-neutral-subtle,#9ea1b1)] whitespace-nowrap">
              <FormattedMessage
                id="a/Br6a"
                defaultMessage="UTC"
                description="UTC timezone indicator"
              />
            </span>
          </div>
        )}

        <span className="text-[var(--color-foreground-neutral-subtle,#9ea1b1)] text-xs mt-2">
          {isDaily ? (
            <FormattedMessage
              id="XVsBTd"
              defaultMessage="Syncs once daily at the selected time (UTC)."
              description="Helper text for daily sync frequency"
            />
          ) : (
            <FormattedMessage
              id="/+k+VV"
              defaultMessage="Syncs every {frequency}, starting from when the connection is saved."
              description="Helper text for hourly sync frequency"
              values={{ frequency: frequencyLabel }}
            />
          )}
        </span>
      </div>
    </FormSection>
  );
}
